/**
 * Whether plans can be recognized here, and through what. The company's service is not connected yet
 * (docs/tasks/floorplan-recognition/README.md): until `RECOGNITION_SERVICE` names it, the entry points stay hidden and
 * the web sends nothing anywhere.
 *
 * `?recognition=demo` plays the flow for this tab with the example house (`demo.ts`; `recognitionScore` and
 * `recognitionFail` steer it), `?recognition=off` ends that, and in development `?recognitionUrl=` points the web at a
 * local stand-in of the service (`tools/e2e-plan-recognition.mjs` runs one).
 */
import { createDemoTransport } from './demo';
import { createHttpTransport } from './http';
import type { FailureCode, RecognitionTransport } from './protocol';

/** The recognition service's root; the patch in the task fills it once the service answers. */
export const RECOGNITION_SERVICE = '';

export type RecognitionMode = 'service' | 'demo';

interface Override { demo?: true; url?: string; score?: number; fail?: FailureCode }

const KEY = 'hp.recognition';
const FAILURES = new Set(['not_recognized', 'timed_out', 'bad_image', 'too_large', 'unsupported', 'too_many', 'busy', 'network', 'server']);

function readOverride(): Override | null {
  if (typeof location === 'undefined') return null;
  let stored: Override | null = null;
  try { stored = JSON.parse(sessionStorage.getItem(KEY) ?? 'null') as Override | null; } catch { stored = null; }
  const params = new URLSearchParams(location.search);
  const mode = params.get('recognition');
  const url = import.meta.env?.DEV ? params.get('recognitionUrl') : null;
  if (mode === 'off') stored = null;
  else if (mode === 'demo' || url) {
    const score = Number(params.get('recognitionScore') ?? NaN), fail = params.get('recognitionFail');
    stored = { ...(mode === 'demo' ? { demo: true as const } : { url: url! }),
      ...(Number.isFinite(score) ? { score: Math.min(100, Math.max(0, score)) } : {}),
      ...(fail && FAILURES.has(fail) ? { fail: fail as FailureCode } : {}) };
  }
  try { if (stored) sessionStorage.setItem(KEY, JSON.stringify(stored)); else sessionStorage.removeItem(KEY); } catch { /* the tab keeps it in memory */ }
  return stored;
}

const override = readOverride();

export function recognitionMode(): RecognitionMode | null {
  if (override?.demo) return 'demo';
  return override?.url || RECOGNITION_SERVICE ? 'service' : null;
}

export function createRecognitionTransport(): RecognitionTransport | null {
  if (override?.demo) return createDemoTransport({ score: override.score, fail: override.fail ?? null });
  const url = override?.url || RECOGNITION_SERVICE;
  return url ? createHttpTransport(url) : null;
}
